import { useCallback, useEffect, useRef } from 'react';
import type { PartialDesignBrief } from '../types/briefSchema';

export type BriefUpdate =
    | Partial<PartialDesignBrief>
    | ((prev: PartialDesignBrief) => Partial<PartialDesignBrief>);

/**
 * Wraps `onBriefUpdate` so multiple sequential `update` calls within the same
 * async flow compose correctly. Callers that need to read the current brief
 * (e.g. to append to an array field) should pass a function form:
 *
 *   update((prev) => ({ referenceImages: [...(prev.referenceImages ?? []), x] }))
 *
 * which receives the most recent value — including any pending updates not yet
 * flushed back through props.
 */
export function useBriefMutator(
    designBrief: PartialDesignBrief,
    onBriefUpdate: (brief: PartialDesignBrief) => void
) {
    const briefRef = useRef(designBrief);
    useEffect(() => {
        briefRef.current = designBrief;
    }, [designBrief]);

    const update = useCallback(
        (updates: BriefUpdate) => {
            const patch = typeof updates === 'function' ? updates(briefRef.current) : updates;
            const updated = { ...briefRef.current, ...patch, updatedAt: new Date() };
            briefRef.current = updated;
            onBriefUpdate(updated);
        },
        [onBriefUpdate]
    );

    return { update };
}
